<template>
    <div id="inputs-audience-view" class="position-relative">
        <div id="choose" class="row">
            <div class="col-12 col-lg-6 mb-4 mb-lg-0">
                <div id="choose-audience" class="card h-100">
                    <div id="choose-audience__description" class="d-flex justify-content-between align-items-center">
                        <h3 class="mb-0">Available Audiences</h3>
                        <button v-if="client && client.url" type="button" class="btn btn-primary btn-sm px-3" @click="openDescribeSegmentModal">
                            <img class="icon" src="/img/icons/avid-icons/edna.svg" alt=""> Add Audience
                        </button>
                    </div>

                    <div id="choose-audience_search-row" class="d-flex flex-column flex-sm-row gap-2 align-items-stretch">
                        <div class="search-box flex-grow-1 position-relative">
                            <input type="text" class="form-control" placeholder="Search by Audience Name or Description ..." aria-label="Search by Audience Name or Description ..." v-model="search_key" @input="handleFilterSelection">
                            <i class="fa fa-search"></i>
                        </div>
                    </div>

                    <div class="flex-grow-1 d-flex flex-column">
                        <div v-if="filtered_audience_list.length === 0" class="card long-card disabled mb-3">
                            <div class="card-body long-card__body row">
                                <div class="long-card__content">
                                    <h4 class="mb-1">No Audiences Found</h4>
                                </div>
                            </div>
                        </div>

                        <draggable 
                            v-else 
                            v-model="available_audience_list" 
                            :group="{ name: 'audiences', pull: 'clone', put: true }"
                            :clone="cloneAudience"
                            item-key="id"
                            id="choose-audience-list"
                            @end="onAvailableDragEnd"
                            class="flex-grow-1"
                        >
                            <template #item="{ element: audience, index: audience_index }">
                                <div class="content-card mb-3" @click="addAudienceSelection(audience, audience_index)" :data-id="audience.id">
                                    <div class="content-card-body">
                                        <h4>{{ audience.name }}</h4>
                                        <div class="audience-info-grid">
                                            <div class="info-item">
                                                <div class="info-label">Contacts</div>
                                                <div class="info-value">{{ audience.type == 'modeled' ? "N/A" : (audience.sizes && audience.sizes.length > 0 ? number_with_zero(audience.sizes[0].size) : 0) }}</div>
                                            </div>
                                            <div class="info-item">
                                                <div class="info-label">Source</div>
                                                <div class="info-value">{{ audience.data_source?.name || 'N/A' }}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </draggable>

                        <div class="table-pagination mt-3 justify-content-center justify-content-md-between">
                            <div class="table-pagination--group d-none d-md-flex">
                                <div class="position-relative">
                                    <select class="form-select" name="num_per_page" id="num_per_page" v-model="num_per_page" @change="reloadPagination" aria-label="Num Per Page">
                                        <option value="5000">ALL</option>
                                        <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                                    </select>
                                    <div class="select-display-text">
                                        Showing {{ num_per_page === '5000' ? 'ALL' : num_per_page }}
                                    </div>
                                </div>
                            </div>

                            <div class="table-pagination--group d-none d-lg-flex">
                                {{ filtered_audience_list.length === 0 ? '0' : item_interval }} of {{ filtered_audience_list.length }}
                            </div>

                            <div class="table-pagination--group justify-content-center justify-content-md-end">
                                <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md me-1" @click="moveToPrevPage" :disabled="page_index === 0" data-bs-toggle="tooltip" title="Previous page">
                                    <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="Previous">
                                </button>

                                <div class="d-flex page-selector" v-if="page_index_array.length <= 3">
                                    <button v-for="page in page_index_array" :key="page" class="btn btn-none btn-icon-only-md" :class="{'current-page': page === page_index + 1}" @click="moveToPage(page)">
                                        {{ page }}
                                    </button>
                                </div>
                                <div class="d-flex page-selector" v-else>
                                    <button v-for="page in page_index_array.slice(0,2)" :key="page" class="btn btn-none btn-icon-only-md" :class="{'current-page': page === page_index + 1}" @click="moveToPage(page)">
                                        {{ page }}
                                    </button>

                                    <button v-if="page_index > 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                                    ...
                                    </button>
                                    <button v-if="page_index >= 2 && page_index < page_index_array.length - 1" class="btn btn-none btn-icon-only-md current-page">
                                        {{ page_index + 1 }}
                                    </button>

                                    <button v-if="page_index < page_index_array.length - 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                                    ...
                                    </button>
                                    <button class="btn btn-none btn-icon-only-md" :class="{'current-page': page_index === page_index_array.length - 1}" @click="moveToPage(page_index_array.length)" :key="page_index_array.length">
                                        {{ page_index_array.length }}
                                    </button>
                                </div>

                                <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md ms-1" @click="moveToNextPage" :disabled="page_index === page_count - 1" data-bs-toggle="tooltip" title="Next page">
                                    <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="Next">
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-12 col-lg-6">
                <div id="selected-audience" class="card gap-0 h-100">
                    <div id="selected-audience-description" class="row d-flex flex-lg-row">
                        <div class="col-11">
                            <h3>Selected Audiences ({{ (playbook.audiences && playbook.audiences.length > 0) ? playbook.audiences.length : 0 }})</h3>
                            <p class="small mb-0">Priority follows the order of your list. Contacts in more than one audience use the highest priority.</p>
                        </div>
                        <div class="col-1">
                            <button type="button" class="btn btn-secondary btn-icon-only-md" @click="showAudienceRationale" data-bs-toggle="tooltip" title="Audience Information">
                                <img class="icon" src="/img/icons/avid-icons/deep-dive.svg" alt="">
                            </button>
                        </div>
                    </div>

                <div id="selected-audience-list" class="flex-grow-1 position-relative" :style="{'max-height': 'calc(100% - 60px)', 'overflow-y': 'auto'}">
                    <div class="position-absolute top-0 start-0 end-0 bottom-0">
                        <div class="position-relative">
                            <div class="scroll-view d-flex flex-column flex-shrink-1 pt-3" style="max-height: 100%;">
                                <draggable 
                                    v-model="playbook.audiences" 
                                    :group="{ name: 'audiences', pull: true, put: true }"
                                    item-key="id"
                                    @end="onSelectedDragEnd"
                                    @add="onAudienceAdded"
                                    class="selected-audiences-dropzone"
                                >
                                    <template #item="{ element: audience, index: audience_index }">
                                        <div class="card long-card me-1 mb-3 draggable-card" :data-id="audience.id">
                                            <div class="card-body long-card__body row">
                                                <div class="long-card__content">
                                                    <div class="d-flex align-items-center position-relative" style="gap: 15px;">
                                                        <div class="long-card-action">
                                                            <div class="audience-order-number">{{ audience_index + 1 }}</div>
                                                            <div class="btn btn-white btn-icon-only-sm handle" data-bs-toggle="tooltip" title="Drag to reorder">
                                                                <img src="/img/icons/avid-icons/drag-n-drop.svg" alt="drag" class="icon">
                                                            </div>
                                                        </div>
                                                        <div style="flex: 1 1 0; min-width:0;">
                                                            <div class="d-flex align-items-center gap-2 mb-2">
                                                                <h4 class="mb-0">{{ audience.name }}</h4>
                                                            </div>
                                                            <div class="audience-info-grid">
                                                                <div class="info-item">
                                                                    <div class="info-label">Contacts</div>
                                                                    <div class="info-value">{{ audience.sizes && audience.sizes.length > 0 ? number_with_zero(audience.sizes[0].size) : 0 }}</div>
                                                                </div>
                                                                <div class="info-item">
                                                                    <div class="info-label">Source</div>
                                                                    <div class="info-value">{{ audience.data_source?.name || 'N/A' }}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div class="long-card-action" style="background-color: #FEE7E5;">
                                                            <button type="button" class="btn btn-none btn-icon-only-sm btn-audience-remove" aria-label="Close" @click.stop="removeAudienceSelection(audience, audience_index)" data-bs-toggle="tooltip" title="Remove audience">
                                                                <img class="icon" src="/img/icons/avid-icons/red-remove.svg" alt="">
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    
                                    <!-- Footer: Empty state when no items, dropzone always visible -->
                                    <template #footer>
                                        <div v-if="playbook.audiences && playbook.audiences.length === 0" class="card long-card disabled mt-3">
                                            <div class="card-body long-card__body row">
                                                <div class="long-card__content">
                                                    <h4 class="mb-1">No Audiences Selected</h4>
                                                    <p>Select an audience from the list on the left to start building your playbook.</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="audience-dropzone mt-3">
                                            <div class="d-flex align-items-center justify-content-center" style="min-height: 100px; padding: 20px;">
                                                <img src="/img/icons/avid-icons/drag-n-drop.svg" alt="drop" class="me-2">
                                                <span class="mb-0 text-muted small">Drop a new audience above to set its place.</span>
                                            </div>
                                        </div>
                                    </template>
                                </draggable>
                            </div>
                        </div>
                    </div>
                </div>

                    <div class="d-flex justify-content-end pt-3" style="border-top: 1px solid #e0e0e0;">
                        <button class="btn btn-primary" @click="saveAudiences">
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <link rel="preload" href="/img/icons/avid-icons/vertical-dots.svg" as="image" type="image/svg+xml" />

        <!-- Audience Rationale Modal -->
        <div class="modal fade" id="audienceRationaleModal" tabindex="-1" aria-labelledby="audienceRationaleModalLabel" aria-hidden="true">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h3 class="modal-title" id="audienceRationaleModalLabel">Audience Information</h3>
                        <button type="button" class="btn btn-white float-end close-modal" data-bs-dismiss="modal" aria-label="Close" data-bs-toggle="tooltip" title="Close">
                            <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                        </button>
                    </div>
                    <div class="modal-body">
                        <h5>How are these audiences used?</h5>
                        <p>Segmentation is essential to any successful fundraising strategy. In this play, we’ve identified several touchpoints that will be tailored to the audiences listed below. This helps ensure each person receives the most relevant message, ultimately improving engagement and results.</p>

                        <h5>Does the audience order matter?</h5>
                        <p>Yes. A single individual may fall into more than one audience. The order numbers indicate priority—so if someone qualifies for multiple audiences, they will receive the touchpoint associated with the highest-priority audience only. They will be excluded from all lower-priority groups to prevent overlap.</p>

                        <h5 v-if="audience_rationale_text">Why these specific audiences?</h5>
                        <p v-if="audience_rationale_text" v-html="audience_rationale_text"></p>
                    </div>
                </div>
            </div>
        </div>

        <DescribeSegmentModal
            v-if="client"
            ref="describeSegmentModalRef"
            :client="client"
            modal-id="play-audience-describe-segment-modal"
            @submitted="onSegmentSuggestionSubmitted"
            @error="onSegmentSuggestionError"
        />
        <SegmentProposalModal
            v-if="client"
            ref="segmentProposalModalRef"
            :client="client"
            @saved="onSegmentProposalSaved"
        />
    </div>
</template>



<script>
import { defineComponent } from 'vue';
import draggable from 'vuedraggable';
import { usePlaybookStore } from '../../stores/playbook';
import { useClientStore } from '../../stores/client';
import statsigService from '../../services/statsig';
import AccordionMultiSelect from '../components/AccordionMultiSelect.vue';
import DescribeSegmentModal from '../pathways/DescribeSegmentModal.vue';
import SegmentProposalModal from '../pathways/SegmentProposalModal.vue';
import { useNotifications } from '../../composables/useNotifications';
import { useAISuggestionPolling, createSegmentSuggestionCheckFunction } from '../../composables/useAISuggestionPolling';

export default defineComponent({
    name: 'InputsAudienceView',
    components: {
        draggable,
        AccordionMultiSelect,
        DescribeSegmentModal,
        SegmentProposalModal,
    },
    setup() {
        const playbookStore = usePlaybookStore()
        const clientStore = useClientStore()
        const { showNotification } = useNotifications()
        const { startPolling, stopPolling } = useAISuggestionPolling()
        return {
            playbookStore,
            clientStore,
            showNotification,
            startPolling,
            stopPolling,
        }
    },
    data() {
        return {
            search_key: '',
            filtered_audience_list: [],
            available_audience_list: [],
            filters: {},
            window_width: window.innerWidth,
            audience_rationale_text: '',
            // pagination
            num_per_page: 10, // default to 10
            num_per_page_options: [10, 20, 30, 50, 100],
            page_count: null,
            page_index: 0,
            page_index_array: [],
            item_interval: '',
            initial_audiences_snapshot: [],
        };
    },
    beforeMount() {
        if(!this.playbook.audiences) {
            this.playbook.audiences = [];
        }
        
        // Convert audience IDs to full audience objects if needed
        this.convertAudienceIdsToObjects();
        
        this.setupFilter();
        this.resetFilteredAudiences();
        // capture snapshot
        this.initial_audiences_snapshot = JSON.parse(JSON.stringify(this.playbook.audiences));
    },
    mounted() {
    },
    beforeUnmount() {
    },
    watch: {
        'playbook.audiences': {
            deep: true,
            handler(newVal, oldVal) {
                // Convert IDs to objects if needed when audiences change
                // Only convert if audiences exist and first item is not an object
                if (newVal && newVal.length > 0 && typeof newVal[0] !== 'object') {
                    this.convertAudienceIdsToObjects();
                }
                this.$emit('audience-changed');
                // Check if there are unsaved changes and emit event
                this.checkAndEmitUnsavedChanges();
            }
        },
        'playbook': {
            handler(newVal, oldVal) {
                // When playbook object changes (e.g., updated from API), convert audience IDs to objects
                if (newVal && newVal.audiences && newVal.audiences.length > 0 && typeof newVal.audiences[0] !== 'object') {
                    this.$nextTick(() => {
                        this.convertAudienceIdsToObjects();
                    });
                }
            },
            deep: false, // Only watch reference changes, not deep changes
            immediate: false
        }
    },
    computed: {
        client() {
            return this.clientStore.getClient;
        },
        playbook() {
            return this.playbookStore.getSelectedPlaybook;
        },
        availableAudiences() {
            return this.playbookStore.getAvailableAudiences;
        },
        source_list() {
            // Get unique sources from available audiences
            const usedSources = new Set();
            this.availableAudiences.forEach(audience => {
                if (audience.data_source && audience.data_source.name) {
                    usedSources.add(audience.data_source.name);
                }
            });
            return Array.from(usedSources).sort().map(source => ({
                value: source,
                text: source,
                id: source
            }));
        },
    },
    methods: {
        openDescribeSegmentModal() {
            this.$refs.describeSegmentModalRef?.show();
        },
        onSegmentSuggestionSubmitted({ segment_suggestion_id }) {
            this.showNotification({
                icon: '/img/icons/nav-icons/Edna.svg',
                text: "We're creating your audience. You'll be notified when it's ready!",
                type: 'info',
                noDismiss: true,
            });
            const requestKey = `segment-suggestion-${segment_suggestion_id}`;
            this.startPolling({
                requestKey,
                checkFn: createSegmentSuggestionCheckFunction({
                    clientUrl: this.client.url,
                    segmentSuggestionId: segment_suggestion_id,
                }),
                onSuccess: (data) => {
                    this.showNotification({
                        icon: '/img/icons/nav-icons/Edna.svg',
                        text: "Your audience suggestion is ready. Click to view.",
                        type: 'success',
                        action: { type: 'openSegmentProposal', segment_suggestion_id: data.id },
                    });
                },
                onTimeout: () => {},
            });
        },
        onSegmentSuggestionError(message) {
            this.showNotification({ text: message || 'Failed to submit.', type: 'error' });
        },
        onSegmentProposalSaved({ segment }) {
            if (segment) {
                const current = this.playbookStore.getAvailableAudiences || [];
                this.playbookStore.setAvailableAudiences([segment, ...current]);
                this.handleFilterSelection();
            }
        },
        convertAudienceIdsToObjects() {
            // Check if audiences are just IDs (numbers or strings that are numbers)
            if (this.playbook.audiences && this.playbook.audiences.length > 0 && 
                typeof this.playbook.audiences[0] !== 'object') {
                
                // Convert IDs to full audience objects
                const convertedAudiences = [];
                
                this.playbook.audiences.forEach(audienceId => {
                    // Find the full audience object from available audiences
                    const fullAudience = this.availableAudiences.find(audience => 
                        audience.id === parseInt(audienceId) || audience.id === audienceId
                    );
                    
                    if (fullAudience) {
                        convertedAudiences.push(fullAudience);
                    }
                });
                
                // Update the playbook audiences with full objects
                this.playbook.audiences = convertedAudiences;
            }
        },
        
        setupFilter() {
            // Initialize filters with no options selected (empty array)
            this.filters = {
                source: []
            };
        },
        resetFilteredAudiences() {
            this.search_key = '';
            this.filters.source = [];
            // Get all available audiences (excluding already selected ones)
            this.filtered_audience_list = this.availableAudiences.filter(audience => {
                return !this.playbook.audiences.some(selected => selected.id === audience.id);
            });
            this.filtered_audience_list.sort((a, b) => (a.name > b.name) ? 1 : -1);
            this.reloadPagination();
        },
        handleFilterSelection() {
            let filtered_list = [];

            if (this.availableAudiences.length === 0) {
                return;
            }
            
            // If no filters are selected, show all items (only apply search filter)
            const noSourceFilter = this.filters.source.length === 0;
            
            this.availableAudiences.forEach(audience => {
                // Skip if already selected
                if (this.playbook.audiences.some(selected => selected.id === audience.id)) {
                    return;
                }
                
                // Check search filter
                let in_search = this.search_key === '' || 
                    audience.name.toLowerCase().includes(this.search_key.toLowerCase()) ||
                    (audience.value_prop && audience.value_prop.toLowerCase().includes(this.search_key.toLowerCase())) ||
                    (audience.source && audience.source.toLowerCase().includes(this.search_key.toLowerCase()));

                // Check source filter (if any source filters are selected)
                let source_match = noSourceFilter || this.filters.source.some(filter => filter.value === audience.data_source?.name);
                
                if (in_search && source_match) {
                    filtered_list.push(audience);
                }
            });

            this.filtered_audience_list = filtered_list.sort((a, b) => (a.name > b.name) ? 1 : -1);
            this.reloadPagination();
            this.moveToPage(1);
        },
        searchAudience() {
            // Delegate to handleFilterSelection to maintain consistency
            this.handleFilterSelection();
        },
        reloadPagination() {
            this.page_count = Math.ceil(this.filtered_audience_list.length / this.num_per_page);
            this.page_index_array = Array.from({length: this.page_count}, (_, i) => i + 1);
            this.getItemInterval();
        },
        getItemInterval() {
            const from = this.page_index * this.num_per_page + 1;
            let to = (this.page_index + 1) * this.num_per_page;
            if (to > this.filtered_audience_list.length) {
                to = this.filtered_audience_list.length;
            }
            this.item_interval = from >= to ? to : `${from} - ${to}`;
            this.available_audience_list = this.filtered_audience_list.slice(from - 1, to);
        },
        moveToPrevPage() {
            this.page_index--;
            this.getItemInterval();
        },
        moveToPage(page) {
            this.page_index = page - 1;
            this.getItemInterval();
        },
        moveToNextPage() {
            this.page_index++;
            this.getItemInterval();
        },
        addAudienceSelection(audience, index) {
            this.playbookStore.addSelectedAudience(audience);
            
            // Remove from filtered list
            const audience_id = audience.id;
            const audience_index = this.filtered_audience_list.findIndex(aud => aud.id === audience_id);
            if (audience_index !== -1) {
                this.filtered_audience_list.splice(audience_index, 1);
            }
            
            this.reloadPagination();
        },
        removeAudienceSelection(audience, index) {
            this.playbookStore.removeSelectedAudience(audience);
            
            // Reapply filters to add the audience back if it matches
            this.handleFilterSelection();
        },
        saveAudiences() {
            // Show SweetAlert warning about touchpoint changes
            Swal.fire({
                title: 'Save Audience Changes?',
                html: `
                <div>
                    <strong>Important:</strong> Any audiences that were removed will have all of their touchpoints deleted, and any audiences added will automatically get new touchpoints created.
                </div>
                `,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Save',
                cancelButtonText: 'Cancel',
                customClass: {
                    popup: 'swal-warning'
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    this.updatePlaybook();
                }
            });
        },
        
        cloneAudience(audience) {
            // Return a deep copy of the audience for dragging
            return JSON.parse(JSON.stringify(audience));
        },
        
        onAvailableDragEnd(event) {
            // Handle drag end from available list
            if (event.from === event.to) {
                // Item was reordered within the available list
                // console.log('Item reordered within available list');
                // Update the filtered list to match the new order
                this.updateFilteredListOrder();
            } else {
                // Item was dragged to selected list (cloned)
                // console.log('Item cloned to selected list');
                // The cloned item is automatically added to selected list
                // Remove it from available list using the original item
                const draggedAudience = this.available_audience_list[event.oldIndex];
                if (draggedAudience) {
                    this.addAudienceSelection(draggedAudience, event.oldIndex);
                }
            }
        },
        
        onAudienceAdded(event) {
            // Handle when an audience is added to the selected list (from available list)
            if (event.added) {
                const addedAudience = event.added.element;
                
                // Remove from available list if it exists there
                const audienceIndex = this.filtered_audience_list.findIndex(aud => aud.id === addedAudience.id);
                if (audienceIndex !== -1) {
                    this.filtered_audience_list.splice(audienceIndex, 1);
                    this.reloadPagination();
                }
            }
        },
        
        onSelectedDragEnd(event) {
            // Handle drag end from selected list
            if (event.from === event.to) {
                // Item was reordered within the same list
                // console.log('Item reordered within selected list');
            } else {
                // Item was dragged to a different list (back to available)
                // console.log('Item dragged back to available list');
                // The item was automatically removed from selected list by draggable
                // We need to add it back to available list
                // Since the item was removed, we need to find it from the original data
                const draggedAudienceId = event.item.getAttribute('data-id') || 
                                        event.item.querySelector('[data-id]')?.getAttribute('data-id');
                
                if (draggedAudienceId) {
                    const fullAudience = this.availableAudiences.find(audience => 
                        audience.id === parseInt(draggedAudienceId)
                    );
                    
                    if (fullAudience) {
                        // Add back to filtered list
                        this.filtered_audience_list.push(fullAudience);
                        this.filtered_audience_list.sort((a, b) => (a.name > b.name) ? 1 : -1);
                        
                        this.reloadPagination();
                    }
                }
            }
        },
        
        updateFilteredListOrder() {
            // Update the filtered_audience_list to match the new order of available_audience_list
            // This ensures the pagination and search still work correctly
            const startIndex = this.page_index * this.num_per_page;
            
            // Update the corresponding slice in filtered_audience_list
            this.filtered_audience_list.splice(startIndex, this.available_audience_list.length, ...this.available_audience_list);
        },
        
        updatePlaybook() {
            // Create a payload with the updated playbook audiences (IDs only for API)
            const payload = {
                ...this.playbook,
                audiences: this.playbook.audiences.map(audience => audience.id)
            };
            
            // Check if audiences changed by comparing current audiences with initial snapshot
            const currentAudienceIds = this.playbook.audiences.map(a => a.id).sort();
            const initialAudienceIds = (this.initial_audiences_snapshot || []).map(a => a.id).sort();
            const audiencesChanged = JSON.stringify(currentAudienceIds) !== JSON.stringify(initialAudienceIds);

            // Send the update request using axios
            window.axios.put(`/api/playbooks/${this.client.url}/${this.playbook.url}`, payload)
                .then(response => {
                    if (response.status === 200) {
                        // Track copy regeneration if audiences changed
                        if (audiencesChanged && statsigService.getClient()) {
                            statsigService.logEvent('playbook_copy_regenerated', null, {
                                playbook_id: this.playbook?.id,
                                playbook_type_name: this.playbook?.playbook_type?.name,
                                source: 'audience_change'
                            });
                        }
                        
                        // Store the current UI audiences before updating the store
                        const currentUIAudiences = [...this.playbook.audiences];
                        
                        // Update the store with the new data (which has IDs)
                        this.playbookStore.setSelectedPlaybook(response.data.playbook);
                        
                        // Restore the full audience objects in the UI
                        this.playbook.audiences = currentUIAudiences;
                        this.initial_audiences_snapshot = JSON.parse(JSON.stringify(this.playbook.audiences));
                        this.$emit('audience-saved');
                        // Emit that changes are cleared after save
                        this.$emit('audience-unsaved-changed', false);

                        // Show success message
                        this.showNotification({
                            text: 'Your audience selections were successfully saved!'
                        });
                    }
                })
                .catch(error => {
                    console.error('Error updating playbook:', error);
                    
                    // Show error message
                    this.showNotification({
                        text: 'Your audience selections failed to save. Please try again.',
                        type: 'error'
                    });
                });
        },
        hasUnsavedChanges() {
            // Compare current audiences with initial snapshot
            const currentAudienceIds = (this.playbook.audiences || []).map(a => a.id).sort();
            const initialAudienceIds = (this.initial_audiences_snapshot || []).map(a => a.id).sort();
            return JSON.stringify(currentAudienceIds) !== JSON.stringify(initialAudienceIds);
        },
        checkAndEmitUnsavedChanges() {
            // Emit event to parent about unsaved changes status
            this.$emit('audience-unsaved-changed', this.hasUnsavedChanges());
        },
        resetUnsavedChanges() {
            this.playbook.audiences = JSON.parse(JSON.stringify(this.initial_audiences_snapshot));
            this.convertAudienceIdsToObjects();
            this.resetFilteredAudiences();
            // Emit that changes are cleared
            this.$emit('audience-unsaved-changed', false);
        },
        isSuggestedAudience(audience) {
            // Check if this audience is in the playbook_type's suggested audiences
            if (!this.playbook.playbook_type || !this.playbook.playbook_type.audiences || !Array.isArray(this.playbook.playbook_type.audiences)) {
                return false;
            }
            
            // If audience doesn't have template_id, it wasn't created from a template, so it's not suggested
            if (!audience.template_id) {
                return false;
            }
            
            // Extract template IDs from the playbook_type.audiences array
            // The array contains template audience objects like {id: template_id, name: Y, ...}
            const templateIds = this.playbook.playbook_type.audiences.map(a => {
                if (typeof a === 'object' && a !== null) {
                    // Handle object format {id: template_id} or {id: template_id, name: Y, ...}
                    return a.id;
                }
                // Handle direct ID format (though unlikely for template audiences)
                return a;
            }).filter(id => id !== null && id !== undefined);
            
            // Check if the current audience's template_id matches any template ID from the playbook type
            // The audience (segment) should have a template_id field if it was created from a template
            return templateIds.includes(audience.template_id);
        },
        showAudienceRationale(event) {
            // Get the rationale from playbook_type
            if (this.playbook.playbook_type && this.playbook.playbook_type.audience_rationale) {
                this.audience_rationale_text = this.playbook.playbook_type.audience_rationale;
            } else {
                this.audience_rationale_text = '';
            }
            
            // Show the modal using Bootstrap
            const modalElement = document.getElementById('audienceRationaleModal');
            if (modalElement) {
                const modal = new window.bootstrap.Modal(modalElement);
                modal.show();
            }
        }
    }
});
</script>